import React, {useEffect, useState} from 'react';
import TaskModel, {TaskConstraintType} from "../../../../../models/responses/task.model";
import styles from '../GanttChart.module.scss';
import {Dropdown, FormField, Input, Select, Tab, TabPane} from 'semantic-ui-react'
import {TaskType} from "../../../../../models/task-type";
import {useTypedSelector} from "../../../../../store/selectors/selectors.utils";
import WbsTree from "../../WbsTree/WbsTree";
import {
    useActiveProjectCalendarsSelector,
    useActiveProjectSelector,
    useCpmMapSelector
} from "../../../../../store/selectors/project.selectors";
import {CpmTaskModel} from "../../../../../models/responses/cpm-task.model";
import TaskStatusModel from "../../../../../models/responses/task-status.model";
import {
    convertDateToIndex,
    convertIndexToSeconds
} from "../../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSquare} from "@fortawesome/pro-light-svg-icons";
import {faCheckSquare} from "@fortawesome/pro-solid-svg-icons";
import {useDispatch} from "react-redux";
import * as taskActions from "../../../../../store/actions/task.actions";
import * as wbsActions from "../../../../../store/actions/wbs.actions";
import {useAllTaskListSelector} from "../../../../../store/selectors/task/task.selectors";
import DatePicker from "react-datepicker";

export default function TaskInfo(props: {
    task: TaskModel,
    activeTaskWbs: any,
    setComponentMounted: (mounted: boolean) => void,
}) {
    const task = props.task;
    const wbsList = useTypedSelector((state) => state.wbs.wbsList)
    const [openWbsTree, setOpenWbsTree] = useState(false);
    const [wbsPlaceholder, setWbsPlaceholder] = useState(task.wbsPath === '' ? task.wbs : task.wbsPath + ' / ' + task.wbs);
    const projectCalendars = useActiveProjectCalendarsSelector();
    const cpmMap = useCpmMapSelector();
    const cpmTask: CpmTaskModel = cpmMap.get(task.task_id);
    const workingHoursPerDay = projectCalendars.get(`whd:${cpmTask.cal_id}`) || 8;
    const dataDateIndex = convertDateToIndex(Math.floor(new Date().getTime() / 86400000) * 86400, cpmTask.cal_id, projectCalendars);
    const activeProject = useActiveProjectSelector();
    const allTasks = useAllTaskListSelector();
    const dispatch = useDispatch();
    const preds = cpmMap.get(`${task.task_id}:preds`);
    const succs = cpmMap.get(`${task.task_id}:succs`);

    const taskTypeConverter = (taskType: TaskType) => {
        switch (taskType) {
            case TaskType.TT_TASK:
                return "Task Dependent";
            case TaskType.TT_MILE:
                return "Start Milestone";
            case TaskType.TT_FIN_MILE:
                return "Finish Milestone";
            case TaskType.TT_RSRC:
                return "Resource Dependent";
            case TaskType.TT_LOE:
                return "Level of Effort";
            default:
                return "Task Dependent";
        }
    }

    const statusConverter = (status: TaskStatusModel) => {
        switch (status) {
            case TaskStatusModel.NOT_STARTED:
                return "not started";
            case TaskStatusModel.IN_PROGRESS:
                return "in progress";
            case TaskStatusModel.SUSPENDED:
                return "in progress";
            case TaskStatusModel.COMPLETE:
                return "complete";
            case TaskStatusModel.DECLARED_COMPLETE:
                return "complete";
            case TaskStatusModel.BLOCK:
                return "not started";
            default:
                return "not started";
        }
    }

    const convertConstraintType = (constraintType: TaskConstraintType) => {
        switch (constraintType) {
            case TaskConstraintType.AS_LATE_AS_POSSIBLE:
                return "As Late As Possible";
            case TaskConstraintType.FINISH_ON_OR_BEFORE:
                return "Finish On or Before";
            case TaskConstraintType.FINISH_ON_OR_AFTER:
                return "Finish On or After";
            case TaskConstraintType.START_ON_OR_BEFORE:
                return "Start On or Before";
            case TaskConstraintType.START_ON_OR_AFTER:
                return "Start On or After";
            case TaskConstraintType.START_ON:
                return "Start On";
            case TaskConstraintType.FINISH_ON:
                return "Finish On";
            case TaskConstraintType.MANDATORY_START:
                return "Must Start On";
            case TaskConstraintType.MANDATORY_FINISH:
                return "Must Finish On";
            case TaskConstraintType.RESUME:
                return "Start On or After";
            default:
                return ''
        }
    }

    useEffect(() => {
        setWbsPlaceholder(task.wbsPath === '' ? task.wbs : task.wbsPath + ' / ' + task.wbs);
    }, [task]);

    const panes = [
        { menuItem: 'Details', render: () =>
                <TabPane>
                    <div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetailSmall}>
                                <label>Task Type:</label>
                                <Input
                                    type="text" value={taskTypeConverter(task.task_type)} readOnly/>
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Task Code:</label>
                                <Input
                                    type="text" value={task.task_code}/>
                            </span>
                            <span className={styles.TaskDetail}>
                                <label>Task Name:</label>
                                <Input
                                    type="text" value={task.task_name}/>
                            </span>
                        </div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetail}>
                                <label>WBS:</label>
                                <Input
                                    type="text" value={wbsPlaceholder} readOnly
                                    onClick={() => setOpenWbsTree(true)}
                                />
                            </span>
                        </div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetailSmall}>
                                <label>Original Duration:</label>
                                <Input
                                    type="text" value={`${task.targetDuration / (workingHoursPerDay * 2)}d`} readOnly
                                />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Remaining Duration:</label>
                                <Input
                                    type="text" value={`${cpmTask.duration / (workingHoursPerDay * 2)}d`} readOnly
                                />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Actual Duration:</label>
                                <Input
                                    type="text" value={task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE ?
                                    // @ts-ignore
                                    `${Math.ceil((cpmTask.ef - cpmTask.es) / (workingHoursPerDay * 2))}d` :
                                    task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED ?
                                        // @ts-ignore
                                        `${Math.ceil((dataDateIndex - cpmTask.es) / (workingHoursPerDay * 2))}d` :
                                        '0d'}
                                    readOnly
                                />
                            </span>
                            <span className={styles.TaskDetail}>
                                <label>Calendar:</label>
                                <Input
                                    type="text" value={
                                    activeProject!.calendars.find((cal: any) => cal.uId === cpmTask.cal_id).calendar_name ||
                                    "No calendar found"} readOnly
                                />
                            </span>
                        </div>
                    </div>
                </TabPane>
        },
        { menuItem: 'Dates', render: () =>
                <TabPane>
                    <div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetailSmall}>
                                <label>Task Status:</label>
                                <Input
                                    type="text" value={cpmTask.status_code} readOnly/>
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Actual Start:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={statusConverter(task.status) !== 'not started' ? task.act_start_date!.toDate() : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly={statusConverter(task.status) !== 'not started'}
                                />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Actual Finish:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={statusConverter(task.status) === 'complete' ?
                                        task.act_end_date ?
                                            task.act_end_date.toDate() :
                                            task.declaredCompleteTimestamp ?
                                                task.declaredCompleteTimestamp.toDate() : undefined : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly={statusConverter(task.status) === 'complete'}
                                />
                            </span>
                            <span className={styles.TaskDetail}></span>
                        </div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetailSmall}>
                                <label>Early Start:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={cpmTask.es !== '' ? new Date(convertIndexToSeconds(cpmTask.es, cpmTask.cal_id, projectCalendars) * 1000) : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly
                                />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Early Finish:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={cpmTask.es !== '' ? new Date(convertIndexToSeconds(cpmTask.ef, cpmTask.cal_id, projectCalendars) * 1000) : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly
                                />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Late Start:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={cpmTask.es !== '' ? new Date(convertIndexToSeconds(cpmTask.ls, cpmTask.cal_id, projectCalendars) * 1000) : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly
                                />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Late Finish:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={cpmTask.es !== '' ? new Date(convertIndexToSeconds(cpmTask.lf, cpmTask.cal_id, projectCalendars) * 1000) : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly
                                />
                            </span>
                            <span className={styles.TaskDetail}></span>
                        </div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetail}>
                                <label>Primary Constraint:</label>
                                <Dropdown
                                    selection
                                    options={Object.values(TaskConstraintType).map((type: TaskConstraintType) => ({
                                        key: type, text: convertConstraintType(type), value: type}))}
                                    value={task.constraint_type === '' ? '' : task.constraint_type!}
                                    readOnly
                                    />
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Constraint Date:</label>
                                <DatePicker
                                    className={styles.TaskDetailDatePicker}
                                    selected={task.constraint_date ? task.constraint_date.toDate() :
                                        task.flConstraintDate ? task.flConstraintDate.toDate() : undefined}
                                    dateFormat="dd-MMM-yyyy"
                                    readOnly
                                />
                            </span>
                        </div>
                    </div>
                </TabPane>
        },
        {
            menuItem: 'Relationships', render: () =>
                <TabPane>
                    <div>
                        <div className={styles.TaskRelationshipsContainer}>
                            <span className={styles.TaskDetail}>
                                <label>Predecessors:</label>
                                <div className={styles.RelationshipListContainer}>
                                <table className={styles.RelationshipList}>
                                    <thead className={styles.RelationshipHeaderRow}>
                                        <tr className={styles.RelationshipTopRow}>
                                            <td className={styles.RelationshipHeaderCellSmall}>Driving</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Critical</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Task ID</td>
                                            <td className={styles.RelationshipHeaderCell}>Task Name</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Type</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Lag</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {preds.map((pred: any) => {
                                        const link = cpmMap.get(pred);
                                        const task = cpmMap.get(link.pred_task_id);
                                        const isDriving = link.ad !== '' && link.ad === cpmTask.ad;
                                        // @ts-ignore
                                        const isCritical = link.ad !== '' && Math.abs(cpmTask.ls - link.ad) <= 2
                                        if (!task || !link) return null;
                                        return (
                                            <tr
                                                onClick={() => {
                                                    const taskData = allTasks.get(task.task_code);
                                                    if (!taskData) return;
                                                    dispatch(taskActions.Actions.setActiveTask(taskData));
                                                    dispatch(wbsActions.Actions.setWBSList(props.activeTaskWbs(taskData)))
                                                    props.setComponentMounted(true);
                                                }}
                                                className={styles.RelationshipRow}
                                            >
                                                <td className={styles.RelationshipCellSmall}>
                                                    <FontAwesomeIcon icon={isDriving ? faCheckSquare : faSquare}/>
                                                </td>
                                                <td className={styles.RelationshipCellSmall}>
                                                    <FontAwesomeIcon icon={isCritical ? faCheckSquare : faSquare}/>
                                                </td>
                                                <td className={styles.RelationshipCellSmall}>{task.p6_id}</td>
                                                <td className={styles.RelationshipCell}>{task.task_name.slice(task.p6_id.length + 3, task.task_name.length)}</td>
                                                <td className={styles.RelationshipCellSmall}>{link.pred_type}</td>
                                                <td className={styles.RelationshipCellSmall}>{`${link.lag_hr_cnt / (workingHoursPerDay * 2)}d`}</td>
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                </table>
                                </div>
                            </span>
                            <span className={styles.TaskDetail}>
                                <label>Successors:</label>
                                <div className={styles.RelationshipListContainer}>
                                <table className={styles.RelationshipList}>
                                    <thead className={styles.RelationshipHeaderRow}>
                                        <tr className={styles.RelationshipTopRow}>
                                            <td className={styles.RelationshipHeaderCellSmall}>Driving</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Critical</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Task ID</td>
                                            <td className={styles.RelationshipHeaderCell}>Task Name</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Type</td>
                                            <td className={styles.RelationshipHeaderCellSmall}>Lag</td>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {succs.map((succ: any) => {
                                        const link = cpmMap.get(succ);
                                        const task = cpmMap.get(link.task_id);
                                        const isDriving = link.ad !== '' && link.ad === task.ad;
                                        const isCritical = link.ad !== '' && Math.abs(task.ls - link.ad) <= 2
                                        if (!task || !link) return null;
                                        return (
                                            <tr
                                                onClick={() => {
                                                    const taskData = allTasks.get(task.task_code);
                                                    if (!taskData) return;
                                                    dispatch(taskActions.Actions.setActiveTask(taskData));
                                                    dispatch(wbsActions.Actions.setWBSList(props.activeTaskWbs(taskData)))
                                                    props.setComponentMounted(true);
                                                }}
                                                className={styles.RelationshipRow}
                                            >
                                                <td className={styles.RelationshipCellSmall}>
                                                    <FontAwesomeIcon icon={isDriving ? faCheckSquare : faSquare}/>
                                                </td>
                                                <td className={styles.RelationshipCellSmall}>
                                                    <FontAwesomeIcon icon={isCritical ? faCheckSquare : faSquare}/>
                                                </td>
                                                <td className={styles.RelationshipCellSmall}>{task.p6_id}</td>
                                                <td className={styles.RelationshipCell}>{task.task_name.slice(task.p6_id.length + 3, task.task_name.length)}</td>
                                                <td className={styles.RelationshipCellSmall}>{link.pred_type}</td>
                                                <td className={styles.RelationshipCellSmall}>{`${link.lag_hr_cnt / (workingHoursPerDay * 2)}d`}</td>
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                </table>
                                </div>
                            </span>
                        </div>
                    </div>
                </TabPane>
        }, {
            menuItem: 'Risks', render: () =>
                <TabPane>
                    <div>
                        <label>Estimating Uncertainty:</label>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetailSmall}>
                                <label>Minimum Duration %:</label>
                                <Input
                                    type="text" value="90%"/>
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Most Likely Duration %:</label>
                                <Input
                                    type="text" value="100%"/>
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Maximum Duration %:</label>
                                <Input
                                    type="text" value="150%"/>
                            </span>
                            <span className={styles.TaskDetail}></span>
                        </div>
                        <div className={styles.TaskDetailRow}>
                            <span className={styles.TaskDetailSmall}>
                                <label>Minimum Duration (days):</label>
                                <Input
                                    type="text" value="90%"/>
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Most Likely Duration (days):</label>
                                <Input
                                    type="text" value="100%"/>
                            </span>
                            <span className={styles.TaskDetailSmall}>
                                <label>Maximum Duration (days):</label>
                                <Input
                                    type="text" value="150%"/>
                            </span>
                            <span className={styles.TaskDetail}></span>
                        </div>
                    </div>
                </TabPane>
        },
        {
            menuItem: 'Codes', render: () =>
                <TabPane>
                    <div>
                        <div className={styles.TaskCodeContainer}>
                            <span className={styles.TaskDetail}>
                                <label>Activity Codes:</label>
                                <div className={styles.CodeListContainer}>
                                    <table className={styles.CodeList}>
                                        <thead className={styles.CodeHeaderRow}>
                                            <tr className={styles.CodeTopRow}>
                                                <td className={styles.CodeHeaderCellSmall}>Library</td>
                                                <td className={styles.CodeHeaderCellSmall}>ID</td>
                                                <td className={styles.CodeHeaderCell}>Description</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {task.activityCodes ? task.activityCodes.map((code: any) =>
                                                <tr className={styles.CodeRow}>
                                                    <td className={styles.CodeCellSmall}>{code.type}</td>
                                                    <td className={styles.CodeCellSmall}>{code.id}</td>
                                                    <td className={styles.CodeCell}>{code.code}</td>
                                                </tr>
                                            )
                                        : null}
                                        </tbody>
                                    </table>
                                </div>
                            </span>
                        </div>
                    </div>
                </TabPane>
        }
    ]

    return (
        <div className={styles.TaskInfoContainer}>
            <Tab panes={panes} />
            {openWbsTree && <WbsTree
                setOpenWbsTree={setOpenWbsTree}
                taskId={task.task_id}
                existingWbs={task.wbs_id}
                setWbsPlaceholder={setWbsPlaceholder}
            />}
        </div>
    )
}